import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Images, Camera, Layers, PackageOpen, ArrowLeft, Settings, AlertTriangle, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { eventsApi } from '../api/events';
import { Event } from '../types';
import { FTPAccountsPanel } from '../components/FTP/FTPAccountsPanel';
import { OverlayManager } from '../components/Overlays/OverlayManager';
import { ExportConfigsPanel } from '../components/ExportConfigs/ExportConfigsPanel';
import { GalleryPanel } from '../components/Gallery/GalleryPanel';
import { EventSettingsPanel } from '../components/Events/EventSettingsPanel';
import { EventErrorsTab } from '../components/Events/EventErrorsTab';
import { TeamManagementPanel } from '../components/Team/TeamManagementPanel';
import { useEventPermissions } from '../hooks/useEventPermissions';
import { LiveBadge } from '../components/UI/LiveBadge';
import { TabBar as UITabBar, type TabItem } from '../components/UI/TabBar';

import { MachhaCharacter } from '../lib/machhaAsset';
import { useAuthStore } from '../store/authStore';
import { track, EVENTS } from '../lib/mixpanel';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './EventDetailPage.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'gallery' | 'overlays' | 'ftp-camera' | 'export' | 'errors' | 'team' | 'settings';

const ALL_TABS: TabItem<Tab>[] = [
  { id: 'gallery',    label: 'Gallery',  icon: Images },
  { id: 'overlays',   label: 'Overlays', icon: Layers },
  { id: 'ftp-camera', label: 'Cameras',  icon: Camera },
  { id: 'export',     label: 'Presets',  icon: PackageOpen },
  { id: 'errors',     label: 'Errors',   icon: AlertTriangle },
  { id: 'team',       label: 'Team',     icon: Users },
  { id: 'settings',   label: 'Settings', icon: Settings },
];

// ─── Compact header ───────────────────────────────────────────────────────────

function CompactHeader({ event }: { event: Event }) {
  const isActive = event.status === 'active';
  const createdBy = typeof event.createdBy === 'object' ? event.createdBy.name : 'Unknown';

  return (
    <motion.div
      className={styles['compact-header']}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className={styles['compact-header-inner']}>
        <div className={styles['compact-title-row']}>
          <h1 className={styles['compact-title']}>
            {event.name}
          </h1>
          <LiveBadge active={isActive} />
        </div>
        <p className={styles['compact-meta']}>
          {new Date(event.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          {' · '}by {createdBy}
          {event.endedAt && (
            <span className={styles['ended-badge']}>
              · ended {new Date(event.endedAt).toLocaleDateString()}
            </span>
          )}
        </p>
      </div>
    </motion.div>
  );
}


// ─── Page ─────────────────────────────────────────────────────────────────────

const slideVariants = {
  initial: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  animate: { x: 0, opacity: 1, transition: { type: 'spring' as const, stiffness: 320, damping: 32, mass: 0.9 } },
  exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0, transition: { duration: 0.18, ease: 'easeIn' as const } }),
};

export const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [event, setEvent] = useState<Event | null>(null);
  usePageTitle(event?.name ?? undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('gallery');
  const [direction, setDirection] = useState(0);

  const isCreator = event
    ? (typeof event.createdBy === 'object' ? event.createdBy._id : event.createdBy) === user?._id
    : false;

  const { permissions } = useEventPermissions(id ?? null, event);
  const canManageTeam = isCreator || permissions.allowManagingTeam;

  // Hide the Team tab when the user has no team-management visibility.
  const TABS: TabItem<Tab>[] = ALL_TABS.filter((t) => (t.id === 'team' ? canManageTeam : true));

  const handleTabChange = (tab: Tab) => {
    const cur = TABS.findIndex(t => t.id === activeTab);
    const next = TABS.findIndex(t => t.id === tab);
    setDirection(next > cur ? 1 : -1);
    setActiveTab(tab);
  };

  useEffect(() => { if (id) loadEvent(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvent = async () => {
    try {
      setLoading(true);
      const [data] = await Promise.all([
        eventsApi.get(id!),
        new Promise(r => setTimeout(r, 600)),
      ]);
      setEvent(data);
      track(EVENTS.EVENT_VIEWED, {
        event_id: data._id,
        event_name: data.name,
        event_status: data.status,
      });
    } catch {
      setError('Failed to load event');
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles['loading-container']}>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: [0, -12, 0], opacity: 1 }}
          transition={{
            y: { repeat: Infinity, duration: 2.8, ease: 'easeInOut' },
            opacity: { duration: 0.4 },
          }}
        >
          <MachhaCharacter state="Loading" size={280} />
        </motion.div>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading event…
        </motion.p>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (error || !event) {
    return (
      <div className={styles['error-container']}>
        <div className={styles['error-content']}>
          <p className={styles['error-text']}>{error || 'Event not found'}</p>
        </div>
        <motion.div
          className={styles['error-mascot']}
          initial={{ x: 40, opacity: 0, rotate: -10 }}
          animate={{ x: 0, opacity: 1, rotate: [-8, -12, -8] }}
          transition={{
            rotate: { repeat: Infinity, duration: 3, ease: 'easeInOut' },
            default: { duration: 0.5 },
          }}
        >
          <MachhaCharacter state="Error" size={160} />
        </motion.div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      className={[styles['page-container'], activeTab === 'gallery' ? styles['page-container-gallery'] : styles['page-container-default']].join(' ')}
    >

      {/* Back + Header row */}
      <div className={[styles['header-row'], activeTab === 'gallery' ? styles['gallery-padded-row'] : ''].join(' ')}>
        <motion.button
          onClick={() => navigate('/events')}
          className={styles['back-button']}
          whileTap={{ scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          aria-label="Back to events"
        >
          <ArrowLeft className={styles['icon-sm']} />
        </motion.button>

        <CompactHeader event={event} />
      </div>

      {/* Tabs */}
      <div className={activeTab === 'gallery' ? styles['gallery-padded-row'] : ''}>
        <UITabBar tabs={TABS} activeTab={activeTab} onChange={handleTabChange} variant="pill" layoutId="event-tab" className={styles['tab-bar-spacing']} />
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={activeTab}
          custom={direction}
          variants={slideVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {activeTab === 'gallery' && (
            <GalleryPanel
              eventId={event._id}
              eventStatus={event.status}
              event={event}
              isCreator={isCreator}
              canCreateAlbum={isCreator || permissions.allowCreatingNewAlbum}
              canDeletePhotos={isCreator || permissions.allowDeletingOfPhotos}
            />
          )}
          {activeTab === 'overlays' && (
            <OverlayManager
              eventId={event._id}
              eventStatus={event.status}
              canManage={isCreator || permissions.allowAddingNewPresets}
            />
          )}
          {activeTab === 'ftp-camera' && (
            <FTPAccountsPanel
              eventId={event._id}
              eventStatus={event.status}
              canManage={isCreator || permissions.allowAddingNewFtpAccount}
            />
          )}
          {activeTab === 'export' && (
            <ExportConfigsPanel
              eventId={event._id}
              canManage={isCreator || permissions.allowAddingNewPresets}
            />
          )}
          {activeTab === 'errors' && (
            <EventErrorsTab eventId={event._id} />
          )}
          {activeTab === 'team' && canManageTeam && (
            <TeamManagementPanel eventId={event._id} canManageTeam={canManageTeam} />
          )}
          {activeTab === 'settings' && (
            <EventSettingsPanel event={event} onEventUpdated={setEvent} permissions={permissions} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
